/**
 * http request模块
 * 支持：fetch API和XMLHttpRequest请求方式
 */

import queryString from './query-string';

export type RequestMethod =
  | 'GET'
  | 'HEAD'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'OPTIONS';

export type RequestHeaders = Record<string, string>;

export type RequestType = 'json' | 'text' | 'urlencoded' | 'form';

export type RequestDataType = 'json' | 'text';

export type RequestContentType = string;

export interface RequestOptions {
  // url地址
  url: string;
  // 数据
  data?: Record<string, any> | string;
  // 请求源站
  origin?: string;
  // 请求method
  method?: RequestMethod;
  // header头
  headers?: RequestHeaders;
  // 请求发送的数据类型
  type?: RequestType;
  // 接收数据类型，默认json
  dataType?: RequestDataType;
  // content-type
  contentType?: RequestContentType;
  // 请求超时时间
  timeout?: number;
  // 是否使用XMLHttpRequest。默认使用fetch
  useAjax?: boolean;
  // 跨域请求是否携带cookie
  credentials?: boolean;
  onProgress?: (loaded: number, total: number) => void;
  onUploadProgress?: (evt: ProgressEvent) => void;
}

export interface ResponseError extends Error {
  code: number;
  message: string;
  data?: any;
}

// 绝对地址正则，匹配以下绝对地址
// `http://xxx`
// `https://xxx`
// `//xxx`
const ABSOLUTE_PATH_REG = /^(?:http[s]?:)?\/\/(.*?)/i;

// dataType与Content-Type的映射关系
const contentTypes = {
  json: 'application/json',
  text: 'text/plain',
  urlencoded: 'application/x-www-form-urlencoded',
  form: 'multipart/form-data',
};

const REQUEST_SUCCESS_STATUS = 200;
const REQUEST_300_STATUS = 300;
const REQUEST_CACHE_STATUS = 304;

const REQUEST_ERROR_CODE = 400;
const REQUEST_ERROR_MSG = '未知错误';

const REQUEST_TIMEOUT_CODE = 400;
const REQUEST_TIMEOUT_MSG = '请求超时';

const REQUEST_ABORT_CODE = 400;
const REQUEST_ABORT_MSG = '请求被取消';

export class ResponseError extends Error implements ResponseError {
  constructor(code: number, message: string, data?: any) {
    super(message);
    this.code = code;
    this.data = data;
  }
}

class Request {
  _url = '';
  _data: any = null;
  _type: RequestType = 'json';
  _dataType: RequestDataType = 'json';
  headers: RequestHeaders = {
    Accept: contentTypes.json,
    'Content-Type': contentTypes.json,
  };

  method: RequestMethod = 'GET';
  timeout = 0;
  useAjax = false;
  cors = false;
  credentials = false;
  origin?: string;
  onProgress?: (loaded: number, total: number) => void;
  onUploadProgress?: (evt: ProgressEvent) => void;

  // XHR请求客户端
  xhr: XMLHttpRequest | null = null;
  // fetch API模拟超时的计时器
  fetchTimeoutTimer = 0;

  constructor(options: RequestOptions) {
    if (options.method) this.method = options.method;
    if (options.type) this.type = options.type;
    if (options.dataType) this.dataType = options.dataType;
    if (options.contentType !== undefined) this.contentType = options.contentType;
    if (options.timeout !== undefined) this.timeout = Math.max(0, options.timeout);
    if (options.useAjax !== undefined) this.useAjax = options.useAjax;
    if (options.credentials !== undefined) this.credentials = options.credentials;
    if (options.origin !== undefined) this.origin = options.origin;
    if (options.data !== undefined) this.data = options.data;

    this.onProgress = options.onProgress;
    this.onUploadProgress = options.onUploadProgress;

    this.url = this.resolveURL(options.url, options.data);

    // 默认情况下，非跨域请求，强制传递cookie凭证
    if (!this.cors) this.credentials = true;
    // 跨域请求,带上 cookie
    // if (this.cors) {
    //   this.credentials = true;
    // }
    if (options.headers) Object.assign(this.headers, options.headers);
  }

  resolveURL(url: string, data: any) {
    // GET/HEAD请求带了data参数，需要格式化成query-string
    if (this.isQuery() && data) {
      if (typeof data === 'string') {
        url += data.startsWith('?') ? data : `?${data}`;
      } else {
        url += `?${queryString.stringify(data)}`;
      }
    }
    // 如果有源站配置，且当前url不是绝对url
    if (this.origin && !ABSOLUTE_PATH_REG.test(url)) {
      url = `${this.origin}${url}`;
    }
    return url;
  }

  get url() {
    return this._url;
  }

  set url(val: string) {
    this._url = val;
    if (ABSOLUTE_PATH_REG.test(val)) {
      const domain = RegExp.$1;
      // 当api接口域名与当前页面域名不一致，说明存在跨域请求
      // 跨域请求，设置cors属性为true
      if (domain !== location.hostname) {
        this.cors = true;
      }
    }
  }

  get data() {
    return this._data;
  }

  set data(val: any) {
    // GET/HEAD请求，参数数据已序列化在url上
    if (this.isQuery() || val == null) {
      this._data = null;
      return;
    }
    // 需要处理的JSON数据，JSON格式化
    if (this.type === 'json') {
      this._data = JSON.stringify(val);
      return;
    }
    // url编码的表单数据，querystring格式化
    if (this.type === 'urlencoded') {
      this._data = queryString.stringify(val);
      return;
    }
    this._data = val;
  }

  get type() {
    return this._type;
  }

  set type(val: RequestType) {
    this._type = val;
    // if (val === 'form') {
    //   this.contentType = false;
    //   return;
    // }
    this.contentType = contentTypes[val];
  }

  get dataType() {
    return this._dataType;
  }

  set dataType(val: RequestDataType) {
    this._dataType = val;
    const accept = contentTypes[val];
    this.setHeader('Accept', accept || '*/*');
  }

  set contentType(val: RequestContentType) {
    // if (val === false) {
    //   this.removeHeader('Content-Type');
    //   return;
    // }
    this.setHeader('Content-Type', val);
  }

  setHeader(key: string, val: string) {
    this.headers[key] = val;
  }

  removeHeader(key: string) {
    delete this.headers[key];
  }

  isQuery() {
    return ['GET', 'HEAD'].includes(this.method);
  }

  request() {
    // @ts-ignore
    if (window.fetch && !this.useAjax) {
      return this.requestByFetchApi();
    }
    return this.requestByXHR();
  }

  async requestByFetchApi() {
    const settings: RequestInit = {
      body: this.data != null ? this.data : undefined,
      method: this.method,
      headers: this.headers,
      credentials: this.credentials ? 'include' : undefined,
      mode: this.cors ? 'cors' : undefined,
      cache: 'default',
    };

    const tasks = [];

    // 超时限制
    if (this.timeout) tasks.push(this.createFetchTimeout());

    // 取消请求
    const { promise: abortPromise, abort } = this.createFetchAbort();
    tasks.push(abortPromise);

    const fetchPromise = window
      .fetch(this.url, settings)
      .then(res => this.onFetchLoad(res))
      .catch(err => this.onFetchError(err));
    tasks.push(fetchPromise);

    // fetch、timeout、abort三个promise，任意一个率先改变状态，即表示请求结束
    const promise = Promise.race(tasks);
    // 挂载abort方法，便于外部手动abort请求
    (promise as any).abort = abort;

    return promise;
  }

  async requestByXHR() {
    const xhr = new XMLHttpRequest();
    this.xhr = xhr;

    // 设置是否需要发送cookie凭证
    xhr.withCredentials = this.credentials;

    xhr.open(this.method, this.url);

    for (const name in this.headers) {
      xhr.setRequestHeader(name, this.headers[name]);
    }

    xhr.upload.addEventListener('progress', this.progressHandler, false);

    // 包装Promise异步模式
    const promise = new Promise<any>((resolve, reject) => {
      // 错误事件捕捉
      xhr.onerror = this.onXHRError(reject);
      // 取消请求事件捕捉
      xhr.onabort = this.onXHRAbort(reject);
      // 超时限制
      if (this.timeout) {
        xhr.timeout = this.timeout;
        xhr.ontimeout = this.onXHRTimeout(reject);
      }
      xhr.onload = this.onXHRLoad(resolve, reject);
    });

    xhr.send(this.data);

    // 挂载abort方法，便于外部手动abort请求
    (promise as any).abort = xhr.abort.bind(this);

    return promise;
  }

  async onFetchLoad(res: Response) {
    this.finallyFetch();
    const { status } = res;
    const result = await this.parseFetchData(res);
    // <200||>=300异常http status，抛出错误
    if (
      status < REQUEST_SUCCESS_STATUS
      || (status >= REQUEST_300_STATUS && status !== REQUEST_CACHE_STATUS)
    ) {
      throw new ResponseError(
        status,
        res.statusText || REQUEST_ERROR_MSG,
        result,
      );
    }
    return result;
  }

  async parseFetchData(res: Response) {
    let result;
    switch (this.dataType) {
      case 'json':
        result = await this.parseFetchJSONData(res);
        break;
      case 'text':
        result = await this.parseFetchTextData(res);
        break;
      default:
        result = '';
        break;
    }
    return result;
  }

  async parseFetchJSONData(res: Response) {
    try {
      return await res.json();
    } catch (err) {
      return {};
    }
  }

  async parseFetchTextData(res: Response) {
    try {
      return await res.text();
    } catch (err) {
      return '';
    }
  }

  onFetchError(err: any) {
    this.finallyFetch();
    throw err;
  }

  createFetchAbort() {
    let abort!: () => void;
    const promise = new Promise((resolve, reject) => {
      abort = () => {
        reject(new ResponseError(REQUEST_ABORT_CODE, REQUEST_ABORT_MSG));
        this.finallyFetch();
      };
    });
    return { promise, abort };
  }

  createFetchTimeout() {
    return new Promise((resolve, reject) => {
      this.fetchTimeoutTimer = window.setTimeout(() => {
        reject(new ResponseError(REQUEST_TIMEOUT_CODE, REQUEST_TIMEOUT_MSG));
        this.finallyFetch();
      }, this.timeout);
    });
  }

  finallyFetch() {
    if (this.fetchTimeoutTimer) {
      window.clearTimeout(this.fetchTimeoutTimer);
      this.fetchTimeoutTimer = 0;
    }
  }

  onXHRLoad(resolve: (value?: any) => void, reject: (reason?: any) => void) {
    return () => {
      if (!this.xhr) return;
      const { xhr } = this;
      if (xhr.readyState === 4) {
        this.finallyXHR();

        let result: any = xhr.responseText || '';
        if (this.dataType === 'json') {
          result = result ? JSON.parse(result) : {};
        }

        const { status } = xhr;
        // <200||>=300异常http status，抛出错误
        if (
          status < REQUEST_SUCCESS_STATUS
          || (status >= REQUEST_300_STATUS && status !== REQUEST_CACHE_STATUS)
        ) {
          return reject(new ResponseError(
            status,
            xhr.statusText || REQUEST_ERROR_MSG,
            result,
          ));
        }
        resolve(result);
      }
    };
  }

  onXHRError(reject: (err?: any) => void) {
    return () => {
      reject(new ResponseError(REQUEST_ERROR_CODE, REQUEST_ERROR_MSG));
      this.finallyXHR();
    };
  }

  onXHRAbort(reject: (err?: any) => void) {
    return () => {
      reject(new ResponseError(REQUEST_ABORT_CODE, REQUEST_ABORT_MSG));
      this.finallyXHR();
    };
  }

  onXHRTimeout(reject: (err?: any) => void) {
    return () => {
      reject(new ResponseError(REQUEST_TIMEOUT_CODE, REQUEST_TIMEOUT_MSG));
      this.finallyXHR();
    };
  }

  finallyXHR() {
    if (!this.xhr) return;
    // 释放xhr
    this.xhr.upload.removeEventListener('progress', this.progressHandler);
    this.xhr.ontimeout = this.xhr.onload = this.xhr.onerror = this.xhr.onabort = null;
    this.xhr = null;
  }

  progressHandler = (evt: ProgressEvent) => {
    if (this.onUploadProgress) {
      this.onUploadProgress(evt);
    }
    if (this.onProgress) {
      this.onProgress(evt.loaded, evt.total);
    }
  };
}

/**
 * 执行请求
 * @param params
 */
export default function request(options: RequestOptions) {
  const req = new Request(options);
  return req.request();
}
